import { chamar, ppost } from './api';
import { ehNativo } from './nativo';

/**
 * Portal do Cliente (Onda 1): passkey no navegador (WebAuthn). No app das lojas a passkey depende dos domínios associados
 * (roteiro no OPERACAO); até lá, o app nativo segue com o código do e-mail (o botão não aparece).
 */
const paraBuffer = (s: string) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));
const paraB64u = (b: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(b)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

export const temPasskey = () => !ehNativo() && typeof window !== 'undefined' && 'PublicKeyCredential' in window && !!navigator.credentials;

/** Entrada por credencial descobrível: o aparelho escolhe a conta, sem pedir o e-mail antes. */
export async function entrarComPasskey(lembrar: boolean): Promise<{ token?: string }> {
  const o = await ppost<{ challenge: string; rpId: string; timeout: number; userVerification: UserVerificationRequirement }>('/acesso/passkey/opcoes');
  const cred = (await navigator.credentials.get({ publicKey: { challenge: paraBuffer(o.challenge), rpId: o.rpId, timeout: o.timeout, userVerification: o.userVerification } })) as PublicKeyCredential | null;
  if (!cred) throw new Error('cancelado');
  const r = cred.response as AuthenticatorAssertionResponse;
  return ppost('/acesso/passkey', {
    credencial_id: cred.id,
    client_data_json: paraB64u(r.clientDataJSON),
    authenticator_data: paraB64u(r.authenticatorData),
    assinatura: paraB64u(r.signature),
    lembrar,
  });
}

interface OpcoesCadastro {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: 'public-key'; alg: number }[];
  timeout: number;
  attestation: AttestationConveyancePreference;
  authenticatorSelection: AuthenticatorSelectionCriteria;
  excludeCredentials: { type: 'public-key'; id: string }[];
}

/** Review r23 segurança (B3): o cadastro exige prova recente (a senha atual ou o código novo do e-mail). */
export async function cadastrarPasskey(nome: string, prova: { atual?: string; codigo?: string }) {
  const o = await ppost<OpcoesCadastro>('/conta/passkeys/opcoes', prova);
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: paraBuffer(o.challenge),
      rp: o.rp,
      user: { id: paraBuffer(o.user.id), name: o.user.name, displayName: o.user.displayName },
      pubKeyCredParams: o.pubKeyCredParams,
      timeout: o.timeout,
      attestation: o.attestation,
      authenticatorSelection: o.authenticatorSelection,
      excludeCredentials: o.excludeCredentials.map((c) => ({ type: c.type, id: paraBuffer(c.id) })),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('cancelado');
  const r = cred.response as AuthenticatorAttestationResponse;
  return chamar('POST', '/conta/passkeys', { nome, client_data_json: paraB64u(r.clientDataJSON), attestation_object: paraB64u(r.attestationObject) });
}

/** Review r24 segurança (B1): remover a passkey também exige a prova recente (a senha atual ou o código novo do e-mail). */
export async function removerPasskey(id: string, prova: { atual?: string; codigo?: string }) {
  return chamar('DELETE', `/conta/passkeys/${encodeURIComponent(id)}`, prova);
}
